export type WorkflowGraphAncestorEdge = {
  readonly fromNodeId: string;
  readonly toNodeId: string;
};

export type WorkflowGraphAncestor = {
  readonly id: string;
  /** 1 = direct predecessor, 2+ = earlier ancestor. */
  readonly depth: number;
};

/**
 * Ancestor node ids of `nodeId` in a workflow graph (transitive predecessors),
 * breadth-first so direct predecessors come first, each tagged with its `depth`
 * (1 = direct predecessor, 2+ = earlier ancestor).
 *
 * Shared by the client `/`-picker (which needs depth to section "Previous step"
 * vs "Earlier outputs") and server-side param-binding validation, so both derive
 * the same ancestor set and display-name ordering. The runtime strategy walks
 * the persisted run graph over the DB (`getAncestorRunNodeIds`) instead, so it
 * keeps its own async traversal — this pure helper only covers the in-memory
 * definition graph both editors/validators already hold.
 */
export function workflowGraphAncestors(
  edges: readonly WorkflowGraphAncestorEdge[],
  nodeId: string
): WorkflowGraphAncestor[] {
  const directPredecessors = (id: string): string[] =>
    edges.filter((edge) => edge.toNodeId === id).map((edge) => edge.fromNodeId);

  const ordered: WorkflowGraphAncestor[] = [];
  const visited = new Set<string>([nodeId]);
  let frontier = directPredecessors(nodeId);
  let depth = 1;
  while (frontier.length > 0) {
    const nextFrontier: string[] = [];
    for (const ancestorId of frontier) {
      if (visited.has(ancestorId)) {
        continue;
      }
      visited.add(ancestorId);
      ordered.push({ id: ancestorId, depth });
      nextFrontier.push(...directPredecessors(ancestorId));
    }
    frontier = nextFrontier;
    depth += 1;
  }
  return ordered;
}
